/**
 * Syncs auth state to observability context: setContext once the identity is
 * known, updateContext when the tenant settles, resetContext on logout. Must be
 * mounted inside ObservabilityProvider, the SDK providers and AuthProvider.
 *
 * Identity contract (W0.3 · F14 / G14):
 *  • `userId` is the INTERNAL `users.id` UUID from `/users/me` — NOT Clerk's
 *    opaque `user_…` id, which is what `useAuth().user.id` carries and what
 *    this component used to send.
 *  • `tenantId` is the user's `user_tenants` row with `isDefault = true`, else
 *    any membership, read from `/auth/me/portals` (whose query INNER JOINs
 *    `tenants`, so the id provably exists there). It used to be `user.id` —
 *    a value that exists in no tenant table at all.
 *  • Both queries settle after login, so the context is set as soon as the
 *    internal id is known and the tenant is folded in via `updateContext`
 *    when it arrives. Until then the tag is omitted: a missing tag is honest,
 *    a wrong tag is not.
 *
 * Part 22 §119 — unified context.
 */

import {
  resolveTenantId,
  useObservabilityClients,
  useObservabilityContext,
} from '@tonnie/observability';
import { useMyPortals, useUserProfile, useUserSettings } from '@tonnie/sdk';
import { useAuth } from '@/core/contexts/AuthContext';
import { useEffect, useRef } from 'react';
import type { Persona } from '@tonnie/observability';
import { OBSERVABILITY_ENV, OBSERVABILITY_RELEASE } from '@/core/lib/observability-env';

function mapRoleToPersona(role: string): Persona {
  if (role === 'family_master' || role === 'family_member') return 'family';
  return 'talent';
}

export function ObservabilityAuthSync(): null {
  const { user } = useAuth();
  const { data: profile } = useUserProfile();
  const { data: portalsData } = useMyPortals();
  const { data: settings } = useUserSettings();
  const { setContext, updateContext, resetContext } = useObservabilityContext();
  const { analytics } = useObservabilityClients();

  const internalUserId = profile?.id;
  const tenantId = resolveTenantId(portalsData?.portals);
  const persona = mapRoleToPersona(user?.role ?? '');

  const identifiedUserIdRef = useRef<string | null>(null);
  const taggedTenantIdRef = useRef<string | null>(null);

  // "Share Analytics" — applied BEFORE the person is identified, and again
  // whenever they switch it. `undefined` until their settings have loaded.
  const shareAnalytics = settings ? settings.privacy.shareAnalytics !== false : undefined;
  useEffect(() => {
    if (shareAnalytics !== undefined) analytics.setConsent(shareAnalytics);
  }, [analytics, shareAnalytics]);

  useEffect(() => {
    // Signed out (or the profile cache has cleared): drop the identity.
    if (!user || !internalUserId) {
      if (identifiedUserIdRef.current !== null) {
        resetContext();
        identifiedUserIdRef.current = null;
        taggedTenantIdRef.current = null;
      }
      return;
    }

    // Identify only once their analytics choice is known: an opted-out person
    // must never be identified, not even for the one request before it loads.
    if (shareAnalytics === undefined) return;

    if (identifiedUserIdRef.current !== internalUserId) {
      identifiedUserIdRef.current = internalUserId;
      taggedTenantIdRef.current = tenantId ?? null;
      setContext({
        userId: internalUserId,
        ...(tenantId ? { tenantId } : {}),
        persona,
        portal: 'journey',
        env: OBSERVABILITY_ENV,
        release: OBSERVABILITY_RELEASE,
      });
      return;
    }

    // The portals query settled after the identity did — the normal path.
    if (tenantId && tenantId !== taggedTenantIdRef.current) {
      taggedTenantIdRef.current = tenantId;
      updateContext({ tenantId });
    }
  }, [
    user,
    internalUserId,
    tenantId,
    persona,
    shareAnalytics,
    setContext,
    updateContext,
    resetContext,
  ]);

  return null;
}
